import { Fragment, useMemo, useState, type ComponentType } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Chip, FormControlLabel, IconButton, MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell,
  TableContainer, TableFooter, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { Segmenty, type Segment } from '@/components/Segmenty';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useRozliczeniaRok } from '@/lib/useAnalytics';
import { formatCount, formatGrosze, odmiana } from '@/lib/format';
import { MIESIACE } from '@/lib/rozliczenia/miesiac';
import {
  MIESIACE_ROKU_KROTKO, PIERWSZY_ROK_ROZLICZENIOWY, etykietaRoku, lataRozliczeniowe, procent,
} from '@/lib/rozliczenia/rok';
import type { MetrykiRoku, MiesiacRoku, RozliczenieRoku, SwiezoscRozliczen, TydzienRoku } from '@/lib/api/analytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * RESTAURACJE → ROZLICZENIA ROCZNE — arkusz OWNERA „Wykresy Obrotów i Zysków” w Control Center
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * TYLKO ODCZYT. Rok rozliczeniowy wrzesień–sierpień. Serwer składa go z MIESIĘCY ekranu
 * miesięcznego (te same tygodnie i te same kwoty faktur), a miesiące z tygodni kanonicznego
 * silnika. Odwzorowanie arkusza:
 *   „Rentowność” → tabela miesięcy (rozwijana do tygodni) z wierszem roku,
 *   „Obroty” / „Sztuki” → siatka restauracja × miesiąc (Obrót / Posiłki),
 *   Obrót Wrocław = restauracje z Wrocławia; Obrót Moje = cała reszta.
 */

const TYDZIEN = ['tydzień rozliczeniowy', 'tygodnie rozliczeniowe', 'tygodni rozliczeniowych'] as const;
const ROZLICZENIE = ['rozliczenie', 'rozliczenia', 'rozliczeń'] as const;
const liczby = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;
const naglowek = { fontWeight: 600, whiteSpace: 'nowrap' } as const;
const wyroznienie = { bgcolor: 'action.selected' } as const;

type Miara = 'obrot' | 'posilki';
const MIARY: readonly Segment<Miara>[] = [
  { wartosc: 'obrot', etykieta: 'Obrót' },
  { wartosc: 'posilki', etykieta: 'Posiłki' },
];

function dzienMiesiac(data: string): string {
  const [, m, d] = data.split('-');
  return `${d}.${m}`;
}

function rokZAdresu(p: URLSearchParams): number | undefined {
  const r = Number(p.get('rrok'));
  return Number.isInteger(r) && r >= PIERWSZY_ROK_ROZLICZENIOWY && r <= 2099 ? r : undefined;
}

function WyborRoku({ wybrany, ustaw, domyslny }: { wybrany: number; ustaw: (r: number | undefined) => void; domyslny: boolean }) {
  const lata = useMemo(() => {
    const l = lataRozliczeniowe();
    return l.includes(wybrany) ? l : [...l, wybrany].sort((a, b) => a - b);
  }, [wybrany]);
  const ostatni = lata[lata.length - 1] ?? wybrany;
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
      <IconButton aria-label="Poprzedni rok rozliczeniowy" size="small" disabled={wybrany <= PIERWSZY_ROK_ROZLICZENIOWY}
        onClick={() => ustaw(wybrany - 1)}><ChevronLeftIcon /></IconButton>
      <Typography sx={{ fontWeight: 700, minWidth: 110, textAlign: 'center' }} data-rok-etykieta>{etykietaRoku(wybrany)}</Typography>
      <IconButton aria-label="Następny rok rozliczeniowy" size="small" disabled={wybrany >= ostatni}
        onClick={() => ustaw(wybrany + 1)}><ChevronRightIcon /></IconButton>
      <TextField select size="small" label="Rok rozliczeniowy" value={wybrany} sx={{ minWidth: 160 }}
        onChange={(e) => ustaw(Number(e.target.value))}>
        {lata.map((r) => <MenuItem key={r} value={r}>{etykietaRoku(r)}</MenuItem>)}
      </TextField>
      {!domyslny && <Chip size="small" variant="outlined" label="Bieżący rok" onClick={() => ustaw(undefined)} />}
    </Stack>
  );
}

function Kafel({ etykieta, wartosc, format, wyrozniony = false }: {
  etykieta: string; wartosc: number | null; format: (v: number | null) => string; wyrozniony?: boolean;
}) {
  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1.25, minWidth: 170, flex: '1 1 170px', bgcolor: wyrozniony ? 'action.selected' : undefined }} data-kafel={etykieta}>
      <Typography className="sygnal-liczba" sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15, ...liczby }}>
        {wartosc === null
          ? <Tooltip title="Suma ukryta: co najmniej jedno rozliczenie z obrotem nie ma kwot — byłaby niepełna."><span>—</span></Tooltip>
          : <AnimatedNumber value={wartosc} format={format} />}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{etykieta}</Typography>
    </Paper>
  );
}

/** Kolumny arkusza „Rentowność” — te same dla tygodnia, miesiąca i roku. */
function KomorkiMetryk({ m, pogrubione = false }: { m: MetrykiRoku; pogrubione?: boolean }) {
  const s = { ...liczby, ...(pogrubione ? { fontWeight: 700, color: 'text.primary' } : {}) };
  return (
    <>
      <TableCell align="right" sx={s}>{formatCount(m.positions)}</TableCell>
      <TableCell align="right" sx={s} data-wroclaw>{formatGrosze(m.turnoverWroclawGrosze)}</TableCell>
      <TableCell align="right" sx={s} data-moje>{formatGrosze(m.turnoverMojeGrosze)}</TableCell>
      <TableCell align="right" sx={{ ...s, ...wyroznienie, fontWeight: 700 }} data-obrot-total>{formatGrosze(m.turnoverGrosze)}</TableCell>
      <TableCell align="right" sx={s}>{procent(m.shareMoje)}</TableCell>
      <TableCell align="right" sx={s}>{formatGrosze(m.avgMealPriceGrosze)}</TableCell>
      <TableCell align="right" sx={s}>{formatCount(m.deliveryDays)}</TableCell>
      <TableCell align="right" sx={s}>{formatGrosze(m.turnoverPerDayGrosze)}</TableCell>
      <TableCell align="right" sx={s}>{m.positionsPerDay === null ? '—' : m.positionsPerDay.toLocaleString('pl-PL', { maximumFractionDigits: 1 })}</TableCell>
      <TableCell align="right" sx={s}>{formatGrosze(m.marginNetGrosze)}</TableCell>
      <TableCell align="right" sx={s}>{formatGrosze(m.vatGrosze)}</TableCell>
      <TableCell align="right" sx={{ ...s, ...wyroznienie, fontWeight: 700 }} data-fv-brutto>{formatGrosze(m.invoiceGrossGrosze)}</TableCell>
      <TableCell align="right" sx={s}>{m.profitabilityPct === null ? '—' : procent(m.profitabilityPct / 100, 2)}</TableCell>
    </>
  );
}

function WierszMiesiaca({ m, tygodnie }: { m: MiesiacRoku; tygodnie: TydzienRoku[] }) {
  const [otwarty, setOtwarty] = useState(false);
  const przyszly = m.weeksFuture === m.weeks;
  const nazwa = `${MIESIACE[m.month - 1]} ${m.year}`;
  return (
    <>
      <TableRow hover data-miesiac-roku={`${m.year}-${m.month}`} sx={{ opacity: przyszly ? 0.55 : 1 }}>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <IconButton size="small" aria-label={`${otwarty ? 'Zwiń' : 'Rozwiń'} tygodnie: ${nazwa}`} onClick={() => setOtwarty((o) => !o)} sx={{ mr: 0.5, p: 0.25 }}>
            {otwarty ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
          <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>{nazwa}</Typography>
          {!m.closed && !przyszly && <Chip size="small" variant="outlined" label="trwa" sx={{ ml: 1 }} />}
        </TableCell>
        <TableCell align="right" sx={liczby}>{m.weeks}</TableCell>
        <KomorkiMetryk m={m} />
      </TableRow>
      {otwarty && tygodnie.map((w) => (
        <TableRow key={`${w.isoYear}-${w.isoWeek}`} data-tydzien-roku={w.monday} sx={{ opacity: w.future ? 0.5 : 0.85 }}>
          <TableCell sx={{ pl: 6, whiteSpace: 'nowrap' }}>
            <Tooltip title={`Tydzień ISO ${w.isoWeek}/${w.isoYear} · ${w.daysInMonth} z 5 dni pon–pt w tym miesiącu · kwoty tygodnia informacyjnie (faktury są miesięczne)`}>
              <Typography component="span" variant="caption">{dzienMiesiac(w.monday)}–{dzienMiesiac(w.friday)} · tydz. {w.isoWeek}{w.future ? ' (przyszły)' : ''}</Typography>
            </Tooltip>
          </TableCell>
          <TableCell />
          <KomorkiMetryk m={w} />
        </TableRow>
      ))}
    </>
  );
}

function SiatkaRestauracji({ rozliczenia, miesiace, razem }: { rozliczenia: RozliczenieRoku[]; miesiace: MiesiacRoku[]; razem: MetrykiRoku }) {
  const [miara, setMiara] = useState<Miara>('obrot');
  const [pokazZerowe, setPokazZerowe] = useState(false);
  const zObrotem = rozliczenia.filter((s) => s.turnoverGrosze !== 0 || s.positions !== 0);
  const zerowe = rozliczenia.filter((s) => s.turnoverGrosze === 0 && s.positions === 0);
  const widoczne = pokazZerowe ? [...zObrotem, ...zerowe] : zObrotem;
  const wartosc = (v: number) => (miara === 'obrot' ? formatGrosze(v) : formatCount(v));
  return (
    <Stack spacing={1}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {miara === 'obrot' ? 'Obroty' : 'Posiłki'} — restauracje × miesiące
        </Typography>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Segmenty opcje={MIARY} wybrana={miara} onZmiana={setMiara} etykieta="Miara siatki" />
          {zerowe.length > 0 && (
            <FormControlLabel control={<Switch size="small" checked={pokazZerowe} onChange={(e) => setPokazZerowe(e.target.checked)} />}
              label={`Pokaż zerowe (${formatCount(zerowe.length)})`} />
          )}
        </Stack>
      </Stack>
      <Karta>
        <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Table stickyHeader size="small" aria-label="Siatka restauracji i miesięcy roku rozliczeniowego">
            <TableHead>
              <TableRow>
                <TableCell sx={naglowek}>Restauracja / grupa</TableCell>
                <TableCell sx={naglowek}>Miasto</TableCell>
                {MIESIACE_ROKU_KROTKO.map((m) => <TableCell key={m} align="right" sx={naglowek}>{m}</TableCell>)}
                <TableCell align="right" sx={{ ...naglowek, ...wyroznienie, fontWeight: 700 }}>Rok</TableCell>
                {miara === 'obrot' && <TableCell align="right" sx={{ ...naglowek, fontWeight: 700 }}>FV brutto roku</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {widoczne.map((s) => {
                const wartosci = miara === 'obrot' ? s.turnoverByMonthGrosze : s.positionsByMonth;
                return (
                  <TableRow hover key={s.settlementGroupId} data-rozliczenie-roku={s.settlementGroupId}>
                    <TableCell sx={{ minWidth: 170 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.settlementGroupName}</Typography>
                      {s.isGroup && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{s.members.map((c) => c.name).join(' · ')}</Typography>}
                    </TableCell>
                    <TableCell>{s.cities.join(', ') || '—'}</TableCell>
                    {wartosci.map((v, i) => <TableCell key={i} align="right" sx={{ ...liczby, color: v ? undefined : 'text.disabled' }}>{v ? wartosc(v) : '—'}</TableCell>)}
                    <TableCell align="right" sx={{ ...liczby, ...wyroznienie, fontWeight: 700 }} data-rok-wartosc>{wartosc(miara === 'obrot' ? s.turnoverGrosze : s.positions)}</TableCell>
                    {miara === 'obrot' && (
                      <TableCell align="right" sx={{ ...liczby, fontWeight: 700 }}>
                        {s.invoiceGrossGrosze === null
                          ? <Tooltip title="Co najmniej jeden miesiąc tego rozliczenia nie ma kwot faktury."><span>—</span></Tooltip>
                          : formatGrosze(s.invoiceGrossGrosze)}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Razem ({formatCount(zObrotem.length)})</TableCell>
                <TableCell />
                {miesiace.map((m) => (
                  <TableCell key={`${m.year}-${m.month}`} align="right" sx={{ ...liczby, fontWeight: 700, color: 'text.primary' }}>
                    {wartosc(miara === 'obrot' ? m.turnoverGrosze : m.positions)}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ ...liczby, ...wyroznienie, fontWeight: 700, color: 'text.primary' }}>
                  {wartosc(miara === 'obrot' ? razem.turnoverGrosze : razem.positions)}
                </TableCell>
                {miara === 'obrot' && <TableCell align="right" sx={{ ...liczby, fontWeight: 700, color: 'text.primary' }}>{formatGrosze(razem.invoiceGrossGrosze)}</TableCell>}
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Karta>
    </Stack>
  );
}

export function RozliczeniaRoczne({ PanelSwiezosci }: { PanelSwiezosci: ComponentType<{ f: SwiezoscRozliczen }> }) {
  const [parametry, ustawParametry] = useSearchParams();
  const wybrany = useMemo(() => rokZAdresu(parametry), [parametry]);
  const { data, isLoading, isFetching, error } = useRozliczeniaRok(wybrany);

  const ustaw = (r: number | undefined) => {
    const p = new URLSearchParams(parametry);
    if (r === undefined) p.delete('rrok'); else p.set('rrok', String(r));
    ustawParametry(p, { replace: false });
  };

  const start = wybrany ?? data?.period.startYear;
  const tygodnieMiesiaca = useMemo(() => {
    const mapa = new Map<string, TydzienRoku[]>();
    for (const w of data?.weeks ?? []) {
      const k = `${w.year}-${w.month}`;
      mapa.set(k, [...(mapa.get(k) ?? []), w]);
    }
    return mapa;
  }, [data]);
  const t = data?.totals;

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Rozliczenia roczne"
        opis={start !== undefined
          ? `Rok rozliczeniowy ${etykietaRoku(start)} · wrzesień–sierpień${t ? ` · ${formatCount(t.weeks)} ${odmiana(t.weeks, TYDZIEN)}` : ''}`
          : 'Rok rozliczeniowy…'}
        akcja={start !== undefined ? <WyborRoku wybrany={start} ustaw={ustaw} domyslny={wybrany === undefined} /> : undefined}
      />

      {isLoading && <LoadingState label="Składanie roku z tygodni rozliczeniowych (pełny rok to kilkadziesiąt tygodni — do kilkunastu sekund)…" />}
      {error ? <ErrorState error={error} context="Restauracje → Rozliczenia roczne." /> : null}

      {data && t && (
        <>
          <PanelSwiezosci f={data.freshness} />
          {t.weeksFuture > 0 && (
            <Alert severity="info">
              <strong>Rok rozliczeniowy trwa</strong> — {formatCount(t.weeks - t.weeksFuture)} z {formatCount(t.weeks)} {odmiana(t.weeks, TYDZIEN)} już się
              zaczęło. Bieżący i przyszłe miesiące mogą się zmienić (w przyszłych tygodniach są tylko zamówienia opłacone z wyprzedzeniem).
            </Alert>
          )}
          {t.settlementsWithoutAmounts > 0 && (
            <Alert severity="warning">
              <strong>{formatCount(t.settlementsWithoutAmounts)} {odmiana(t.settlementsWithoutAmounts, ROZLICZENIE)} z obrotem bez kwot faktury</strong> w co
              najmniej jednym miesiącu — sumy marży, VAT i FV roku są ukryte, bo byłyby niepełne.
            </Alert>
          )}

          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
            <Kafel etykieta="Obrót roku — wszystkie restauracje" wartosc={t.turnoverGrosze} format={formatGrosze} wyrozniony />
            <Kafel etykieta="Obrót Moje (poza Wrocławiem)" wartosc={t.turnoverMojeGrosze} format={formatGrosze} />
            <Kafel etykieta="Obrót Wrocław" wartosc={t.turnoverWroclawGrosze} format={formatGrosze} />
            <Kafel etykieta="Marża TuSzama netto" wartosc={t.marginNetGrosze} format={formatGrosze} />
            <Kafel etykieta="FV TuSzama brutto" wartosc={t.invoiceGrossGrosze} format={formatGrosze} wyrozniony />
            <Kafel etykieta="Posiłki" wartosc={t.positions} format={formatCount} />
          </Stack>

          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Rentowność — miesiące roku {data.period.label}</Typography>
          <Karta>
            <TableContainer sx={{ maxHeight: '75vh', overflow: 'auto', opacity: isFetching ? 0.6 : 1 }}>
              <Table stickyHeader size="small" aria-label={`Rozliczenia roczne — ${data.period.label}`}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={naglowek}>Miesiąc</TableCell>
                    <TableCell align="right" sx={naglowek}>Tyg.</TableCell>
                    <TableCell align="right" sx={naglowek}>Posiłki</TableCell>
                    <TableCell align="right" sx={naglowek}>Obrót Wrocław</TableCell>
                    <TableCell align="right" sx={naglowek}>Obrót Moje</TableCell>
                    <TableCell align="right" sx={{ ...naglowek, ...wyroznienie, fontWeight: 700 }}>Obrót total</TableCell>
                    <TableCell align="right" sx={naglowek}>Udział Moje</TableCell>
                    <TableCell align="right" sx={naglowek}>Śr. cena posiłku</TableCell>
                    <TableCell align="right" sx={naglowek}>Dni</TableCell>
                    <TableCell align="right" sx={naglowek}>Obrót / dzień</TableCell>
                    <TableCell align="right" sx={naglowek}>Posiłki / dzień</TableCell>
                    <TableCell align="right" sx={naglowek}>Marża netto</TableCell>
                    <TableCell align="right" sx={naglowek}>VAT</TableCell>
                    <TableCell align="right" sx={{ ...naglowek, ...wyroznienie, fontWeight: 700 }}>FV brutto</TableCell>
                    <TableCell align="right" sx={naglowek}>Rentowność</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.months.map((m) => (
                    <Fragment key={`${m.year}-${m.month}`}>
                      <WierszMiesiaca m={m} tygodnie={tygodnieMiesiaca.get(`${m.year}-${m.month}`) ?? []} />
                    </Fragment>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow data-rok-razem>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap' }}>Rok {data.period.label}</TableCell>
                    <TableCell align="right" sx={{ ...liczby, fontWeight: 700, color: 'text.primary' }}>{t.weeks}</TableCell>
                    <KomorkiMetryk m={t} pogrubione />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Karta>

          <SiatkaRestauracji rozliczenia={data.settlements} miesiace={data.months} razem={t} />

          <Typography variant="caption" component="div" sx={{ color: 'text.secondary', lineHeight: 1.6 }} data-odwzorowanie-arkusza>
            Odwzorowanie arkusza „Wykresy Obrotów i Zysków”: <strong>Rentowność</strong> → tabela miesięcy (rozwiń miesiąc, żeby zobaczyć
            tygodnie), <strong>Obroty</strong> i <strong>Sztuki</strong> → siatka restauracji (Obrót / Posiłki). Obrót Wrocław = restauracje z
            Wrocławia, Obrót Moje = wszystkie pozostałe. Miesiąc = pełne tygodnie pon–pt, tydzień należy do miesiąca z większością swoich dni
            roboczych (jak na ekranie miesięcznym — arkusz liczył np. wrzesień 2026 jako tygodnie 36–39). Kwoty miesięcy i roku to faktury
            miesięczne (do grosza); kwoty tygodni są informacyjne. Dni = dni pon–pt z obrotem; posiłki = pozycje zamówień.
            Bez odwzorowania, do decyzji OWNERA: „Marża rzeczywista” (Moje × 16,3% + Wrocław × 5,44%), „Zysk/dzień netto”
            (× 0,988 × 0,15 × 0,77), „Obrót moje bez DarkCraft”, „FV Wrocław”.
          </Typography>
        </>
      )}

      {!isLoading && !error && !data && (
        <EmptyState title="Brak danych" description="Serwer nie zwrócił rozliczenia rocznego." />
      )}
    </Stack>
  );
}
